import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import VisaStatusBadge from './VisaStatusBadge';
import UrgencyIndicator from './UrgencyIndicator';
import { useLanguage } from '../../../contexts/LanguageContext';

const VisaDetailsModal = ({ application, onClose }) => {
  const { t, isRTL } = useLanguage();

  if (!application) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysRemaining = (expirationDate) => {
    const today = new Date();
    const expiry = new Date(expirationDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = calculateDaysRemaining(application?.expirationDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-card rounded-lg border border-border shadow-warm max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">
            {t('visas.visaDetails')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label={t('common.close')}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Pilgrim Info */}
          <div className="flex items-start gap-4 pb-6 border-b border-border">
            <Image
              src={application?.pilgrimPhoto}
              alt={application?.pilgrimPhotoAlt}
              className="w-20 h-20 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">{application?.pilgrimName}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('visas.passportNumber')}: {application?.passportNumber}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <VisaStatusBadge status={application?.status} size="md" />
                <UrgencyIndicator level={application?.urgency} daysRemaining={daysRemaining > 0 ? daysRemaining : 0} />
              </div>
            </div>
          </div>

          {/* Application Details */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {t('visas.applicationDetails')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">{t('visas.applicationDate')}</p>
                <p className="text-sm font-medium text-foreground">{formatDateTime(application?.applicationDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">{t('visas.processingStage')}</p>
                <p className="text-sm font-medium text-foreground">{application?.processingStage}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">{t('visas.expirationDate')}</p>
                <p className="text-sm font-medium text-foreground">{formatDate(application?.expirationDate)}</p>
                {daysRemaining <= 30 && daysRemaining > 0 && (
                  <p className="caption text-warning mt-1">{daysRemaining} {t('visas.daysRemaining')}</p>
                )}
                {daysRemaining <= 0 && (
                  <p className="caption text-destructive mt-1">{t('visas.expired')}</p>
                )}
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="caption text-muted-foreground mb-1">{t('visas.visaStatus')}</p>
                <p className="text-sm font-medium text-foreground capitalize">
                  {application?.status?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {t('visas.statusTimeline')}
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t('visas.applicationSubmitted')}</p>
                  <p className="caption text-muted-foreground">{formatDateTime(application?.applicationDate)}</p>
                </div>
              </div>
              {application?.status === 'in_review' && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Clock" size={16} className="text-warning" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{t('visas.underReview')}</p>
                    <p className="caption text-muted-foreground">{t('visas.currentStage')}</p>
                  </div>
                </div>
              )}
              {application?.status === 'approved' && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{t('visas.approved')}</p>
                    <p className="caption text-muted-foreground">{t('visas.visaIssued')}</p>
                  </div>
                </div>
              )}
              {application?.status === 'rejected' && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="XCircle" size={16} className="text-destructive" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{t('visas.rejected')}</p>
                    <p className="caption text-muted-foreground">{application?.processingStage}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            {application?.status === 'pending' && (
              <>
                <Button variant="primary" iconName="CheckCircle">
                  {t('visas.approve')}
                </Button>
                <Button variant="outline" iconName="XCircle">
                  {t('visas.reject')}
                </Button>
              </>
            )}
            {application?.status === 'in_review' && (
              <Button variant="outline" iconName="FileText">
                {t('visas.requestDocs')}
              </Button>
            )}
            {application?.status === 'approved' && (
              <Button variant="primary" iconName="Download">
                {t('visas.downloadVisa')}
              </Button>
            )}
            <Button variant="ghost" iconName="Printer">
              {t('visas.print')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaDetailsModal;